import React from 'react';
import { flowRight as compose } from 'lodash';
import { NavigateFunction } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

import s from './ExportScreen.module.scss';
import withInterestTestResults from '../HomeScreen/DataWrappers/InterestTestResultsWrapper';
import testCompletionRequired from '../HomeScreen/DataWrappers/TestCompletionRequiredWrapper';
import withSuggestedProfessions from '../HomeScreen/DataWrappers/SuggestedProfessionsWrapper';
import withFavoriteProfessions from '../HomeScreen/DataWrappers/FavoriteProfessionsWrapper';
import withAuthentication from '../Auth/AuthenticationWrapper';
import ContentBody from '../ContentBody/index';
import Header from '../Header/index';
import Footer from '../Footer/index';
import { ROUTE_PREFIX } from '../../constants/router';
import { TEST_MODE_YOUNG } from '../../constants/testModes';
import {
  ItestExportSnapshot,
  buildPdfExportUrl,
  getExportSnapshots,
  getExportSnapshotByTimestamp,
  saveExportSnapshot,
} from '../../util/exportSnapshots';

export const b64toBlob = (b64Data: any, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

const renderQrCode = (pdfUrl: string) => (
  <div className={s.qrCodeContainer}>
    <h2>
      Scanne den QR-Code, um dein PDF auf einem anderen Gerät herunterzuladen
    </h2>
    <QRCodeSVG value={`${window.location.origin}${pdfUrl}`} size={220} />
  </div>
);

const RIASEC_KEYS: Array<'R' | 'I' | 'A' | 'S' | 'E' | 'C'> = [
  'R',
  'I',
  'A',
  'S',
  'E',
  'C',
];

const STRICT_MODE_DEDUPE_WINDOW_MS = 100;

interface ExportScreenProps {
  loggedMode: string;
  meData?: any;
  userItestResults?: any;
  userItestMode?: string;
  favoriteProfessions?: string[];
  favoriteProfessionsFilter?: string;
  navigate: NavigateFunction;
  params: any;
}

interface ExportScreenState {
  view: 'choice' | 'qr';
  snapshot: ItestExportSnapshot | null;
}

class ExportScreen extends React.Component<
  ExportScreenProps,
  ExportScreenState
> {
  static defaultProps = {
    meData: null,
    userItestResults: null,
    userItestMode: null,
    favoriteProfessions: [],
    favoriteProfessionsFilter: null,
  };

  constructor(props: ExportScreenProps) {
    super(props);

    this.state = {
      view: 'choice',
      snapshot: null,
    };

    this.showQrCode = this.showQrCode.bind(this);
    this.showChoice = this.showChoice.bind(this);
  }

  componentDidMount() {
    const { timestamp } = this.props.params;

    if (timestamp) {
      const existingSnapshot = getExportSnapshotByTimestamp(Number(timestamp));
      if (existingSnapshot) {
        this.setState({ snapshot: existingSnapshot });
        return;
      }
    }

    const {
      userItestResults,
      userItestMode,
      favoriteProfessions,
      favoriteProfessionsFilter,
    } = this.props;

    if (!userItestResults) {
      return;
    }

    const riasec = RIASEC_KEYS.map(
      (key) => userItestResults.interestPercentages[key],
    ) as ItestExportSnapshot['riasec'];
    const accuracy = userItestResults.diffScore.percentage;
    const favoriteProfessionIds = (favoriteProfessions || []).map((id) =>
      id.replace('profession-', ''),
    );
    const testMode = userItestMode || TEST_MODE_YOUNG;
    const professionsFilter = favoriteProfessionsFilter || 'E0';

    const existingSnapshots = getExportSnapshots();
    const lastSnapshot = existingSnapshots[existingSnapshots.length - 1];
    const isDuplicateOfLast =
      !!lastSnapshot &&
      Date.now() - lastSnapshot.timestamp < STRICT_MODE_DEDUPE_WINDOW_MS &&
      lastSnapshot.accuracy === accuracy &&
      lastSnapshot.testMode === testMode &&
      lastSnapshot.professionsFilter === professionsFilter &&
      JSON.stringify(lastSnapshot.riasec) === JSON.stringify(riasec) &&
      JSON.stringify(lastSnapshot.favoriteProfessionIds) ===
        JSON.stringify(favoriteProfessionIds);

    let snapshot: ItestExportSnapshot;
    if (isDuplicateOfLast) {
      snapshot = lastSnapshot;
    } else {
      snapshot = {
        timestamp: Date.now(),
        riasec,
        accuracy,
        favoriteProfessionIds,
        testMode,
        professionsFilter,
      };
      saveExportSnapshot(snapshot);
    }

    this.setState({ snapshot });
    this.props.navigate(`${ROUTE_PREFIX}/export/${snapshot.timestamp}`, {
      replace: true,
    });
  }

  showQrCode() {
    this.setState({ view: 'qr' });
  }

  showChoice() {
    this.setState({ view: 'choice' });
  }

  renderChoice(pdfUrl: string) {
    return (
      <div className={s.exportChoiceContainer}>
        <h2>Sichere deine Ergebnisse!</h2>
        <p>Du hast 2 Möglichkeiten:</p>
        <div className={s.exportButtonWrapper}>
          <a href={pdfUrl}>
            <button type="button" className="btn btn-primary">
              PDF herunterladen
            </button>
          </a>
        </div>
        <p>Hol dir deine Ergebnisse mittels QR-Code auf ein anderes Device</p>
        <div className={s.exportButtonWrapper}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.showQrCode}
          >
            QR-Code erstellen
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { loggedMode, meData } = this.props;
    const { view, snapshot } = this.state;

    return (
      <div className={s.exportScreenContainer}>
        <Header
          withBackButton
          onBack={view === 'qr' ? this.showChoice : undefined}
        />
        <ContentBody>
          {snapshot &&
            (view === 'choice'
              ? this.renderChoice(buildPdfExportUrl(snapshot))
              : renderQrCode(buildPdfExportUrl(snapshot)))}
        </ContentBody>
        <Footer loggedMode={loggedMode} meData={meData} />
      </div>
    );
  }
}

export default compose(
  withAuthentication,
  withInterestTestResults,
  testCompletionRequired,
  withSuggestedProfessions,
  (comp: any) => withFavoriteProfessions(comp, true),
)(ExportScreen);
